export interface StoredSkin {
    id: string;
    name: string;
    fileName: string;
    format?: 'glb' | 'gltf' | 'obj' | 'fbx' | 'stl' | 'ply' | 'dae' | '3ds';
    data: ArrayBuffer;
    scale: number;
    yOffset: number;
    rotationY?: number;
    libraryId?: string;
    /** @deprecated 기존 로컬 데이터 호환용. 새 CharDB 캐시는 UID 원문을 저장하지 않는다. */
    nfcIdentifier?: string;
    nfcParticipantId?: string;
    nfcAssetId?: string;
    nfcChecksum?: string;
    nfcFileSize?: number;
    nfcBaseUrl?: string;
    customizationEnabled?: boolean;
    stats?: {
        jump: number;
        steering: number;
    };
    updatedAt: string;
}

export class SkinStorage {
    private readonly databaseName = 'quiz-rush-3d';
    private readonly storeName = 'skins';
    private readonly version = 1;

    public async get(): Promise<StoredSkin | null> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readonly');
            const request = transaction.objectStore(this.storeName).get('active');
            request.onsuccess = () => resolve((request.result as StoredSkin) || null);
            request.onerror = () => reject(request.error || new Error('스킨을 불러오지 못했습니다.'));
            transaction.oncomplete = () => database.close();
        });
    }

    public async put(record: StoredSkin): Promise<void> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readwrite');
            transaction.objectStore(this.storeName).put(record);
            transaction.oncomplete = () => {
                database.close();
                resolve();
            };
            transaction.onerror = () => {
                database.close();
                reject(transaction.error || new Error('스킨을 저장하지 못했습니다.'));
            };
        });
    }

    public async listLocal(): Promise<StoredSkin[]> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readonly');
            const request = transaction.objectStore(this.storeName).getAll();
            request.onsuccess = () => resolve(((request.result as StoredSkin[]) || []).filter((record) => record.id !== 'active'));
            request.onerror = () => reject(request.error || new Error('로컬 캐릭터 목록을 불러오지 못했습니다.'));
            transaction.oncomplete = () => database.close();
        });
    }

    public async deleteLocal(id: string): Promise<void> {
        if (!id || id === 'active') throw new Error('삭제할 로컬 캐릭터 ID가 올바르지 않습니다.');
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readwrite');
            transaction.objectStore(this.storeName).delete(id);
            transaction.oncomplete = () => {
                database.close();
                resolve();
            };
            transaction.onerror = () => {
                database.close();
                reject(transaction.error || new Error('로컬 캐릭터를 삭제하지 못했습니다.'));
            };
        });
    }

    public async clear(): Promise<void> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readwrite');
            transaction.objectStore(this.storeName).delete('active');
            transaction.oncomplete = () => {
                database.close();
                resolve();
            };
            transaction.onerror = () => {
                database.close();
                reject(transaction.error || new Error('스킨을 삭제하지 못했습니다.'));
            };
        });
    }

    public async clearAll(): Promise<void> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readwrite');
            transaction.objectStore(this.storeName).clear();
            transaction.oncomplete = () => {
                database.close();
                resolve();
            };
            transaction.onerror = () => {
                database.close();
                reject(transaction.error || new Error('기존 캐릭터 저장소를 비우지 못했습니다.'));
            };
        });
    }

    private open(): Promise<IDBDatabase> {
        if (typeof indexedDB === 'undefined') {
            return Promise.reject(new Error('이 브라우저는 IndexedDB 스킨 저장을 지원하지 않습니다.'));
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.databaseName, this.version);
            request.onupgradeneeded = () => {
                const database = request.result;
                if (!database.objectStoreNames.contains(this.storeName)) {
                    database.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error || new Error('스킨 저장소를 열지 못했습니다.'));
        });
    }
}
